<template>
  <div class="p-4">
    <!-- Display selected date range -->
    <div
      v-if="dateRange && dateRange.start && dateRange.end"
      class="flex gap-4 mb-4 text-sm text-gray-700"
    >
      <p><strong>Start:</strong> {{ dateRange.start.toLocaleDateString() }}</p>
      <p><strong>End:</strong> {{ dateRange.end.toLocaleDateString() }}</p>
    </div>

    <!-- Calendar container with visual disable -->
    <div :class="{ 'opacity-50 pointer-events-none': !isFrequencyValid }">
      <DatePicker
        v-model="dateRange"
        is-range
        mode="range"
        :attributes="attrs"
        :disabled="!isFrequencyValid"
      />
    </div>

    <!-- Instructional message -->
    <p
      v-if="!isFrequencyValid"
      class="mt-2 text-sm text-gray-500"
    >
      Please select a frequency to enable the date picker.
    </p>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue';
import { useDsaReportsStore } from '../pinia/dsaReportsStore';
import { useAuthStore } from '../pinia/authStore';
import 'v-calendar/dist/style.css';
import { DatePicker } from 'v-calendar';

export default {
  components: {
    DatePicker,
  },
  setup() {
    const myStore = useDsaReportsStore();
    const authStore = useAuthStore();
    const isAuthenticated = computed(() => authStore.isAuthenticated);

    const toDate = (value) => {
      if (!value) return null;
      return value instanceof Date ? value : new Date(value);
    };

    const dateRange = ref({
      start: toDate(myStore.startDate),
      end: toDate(myStore.endDate),
    });
    const attrs = ref(myStore.attrs);
    const isSyncingFromStore = ref(false);

    const isFrequencyValid = computed(() => {
      const validFrequencies = ['On-Demand', 'Bi-Weekly', 'Monthly', 'Quarterly', 'Yearly', 'Fortnightly'];
      return validFrequencies.includes(myStore.selectedFrequency);
    });

    const updateStoreDateRange = (start, end) => {
      if (!dateRange.value) {
        dateRange.value = { start: null, end: null };
      }
      dateRange.value.start = start;
      dateRange.value.end = end;
      myStore.updateStartDate(start);
      myStore.updateEndDate(end);
    };

    const setHighlight = (start, end) => {
      if (start && end) {
        attrs.value = [
          {
            key: 'selectedRange',
            highlight: {
              start: { fillMode: 'solid', color: 'gray' },
              base: { fillMode: 'light', color: 'gray' },
              end: { fillMode: 'solid', color: 'gray' },
            },
            dates: { start, end },
          },
        ];
      } else {
        attrs.value = [];
      }
    };

    setHighlight(dateRange.value.start, dateRange.value.end);

    watch(isAuthenticated, (newVal) => {
      if (!newVal) {
        try {
          dateRange.value = { start: null, end: null };
          attrs.value = [];
        } catch (error) {
          console.error("Error resetting date range, but it's okay:", error);
        }
      }
    });

    watch(dateRange, (newValue) => {
      if (isSyncingFromStore.value) {
        return;
      }
      if (!newValue) {
        setHighlight(null, null);
        return;
      }
      if (newValue.start) {
        const selectedDate = new Date(newValue.start);
        let start = new Date();
        let end;

        switch (myStore.selectedFrequency) {
          case 'On-Demand':
            start = new Date(selectedDate);
            end = new Date(newValue.end);
            break;
          case 'Bi-Weekly': {
            const adjustment = selectedDate.getDay() === 0 ? -6 : 1;
            start = new Date(
              selectedDate.setDate(
                selectedDate.getDate() - selectedDate.getDay() + adjustment
              )
            );
            end = new Date(new Date(start).setDate(start.getDate() + 13));
            break;
          }
          case 'Monthly':
            start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
            end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
            break;
          case 'Quarterly':
            start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
            end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 3, 0);
            break;
          case 'Yearly':
            start = new Date(selectedDate.getFullYear(), 0, 1);
            end = new Date(selectedDate.getFullYear() + 1, 0, 0);
            break;
          case 'Fortnightly':
            start = new Date(selectedDate.getFullYear(), 0, 1);
            end = new Date(selectedDate.getFullYear() + 1, 0, 0);
            break;
          default:
            start = new Date(selectedDate);
            end = new Date(newValue.start);
        }

        updateStoreDateRange(start, end);

        setHighlight(newValue.start, newValue.end);
      }
    });

    watch(
      () => [myStore.startDate, myStore.endDate],
      ([startValue, endValue]) => {
        const start = toDate(startValue);
        const end = toDate(endValue);
        const currentStart = dateRange.value?.start || null;
        const currentEnd = dateRange.value?.end || null;
        if (
          (start?.getTime() || null) === (currentStart?.getTime() || null) &&
          (end?.getTime() || null) === (currentEnd?.getTime() || null)
        ) {
          return;
        }
        isSyncingFromStore.value = true;
        dateRange.value = { start, end };
        setHighlight(start, end);
        isSyncingFromStore.value = false;
      }
    );

    return {
      dateRange,
      attrs,
      myStore,
      isFrequencyValid,
    };
  },
};
</script>

<style scoped>
</style>
